/**
 * useChat Hook
 * Manages chat list state and operations
 */

import { useState, useEffect, useCallback } from "react";
import { ChatService, Chat } from "../services/api/chat";
import { LLMChatService } from "../services/api/llm-chat";

export function useChat() {
  const [chats, setChats] = useState<Chat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch user's chat list
   */
  const fetchChats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const chatList = await ChatService.getUserChats();
      setChats(chatList);
    } catch (err: any) {
      console.error("Failed to fetch chats:", err);
      setError(err.message || "Failed to load chats");
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a new chat
   * Extension format: /start_chat creates chat without title, returns chat_id
   * Title can be set later via rename or will be auto-generated from first message
   */
  const createChat = useCallback(async () => {
    try {
      const response = await ChatService.startChat();

      // Add new chat to the list
      const newChat: Chat = {
        chat_id: response.chat_id,
        chat_name: "New chat", // Default name until first message
        source: response.source,
        created_at: response.created_at,
      };

      setChats((prev) => [newChat, ...prev]);

      return response;
    } catch (err: any) {
      console.error("Failed to create chat:", err);
      throw err;
    }
  }, []);

  /**
   * Delete a chat
   */
  const deleteChat = useCallback(async (chatId: string) => {
    try {
      const response = await ChatService.deleteChat(chatId);

      // Check if the API call was successful
      if (!response.success) {
        throw new Error(response.error || "Failed to delete chat");
      }

      // Remove chat from list only on success
      setChats((prev) => prev.filter((chat) => chat.chat_id !== chatId));

      return { success: true };
    } catch (err: any) {
      console.error("Failed to delete chat:", err);
      throw err;
    }
  }, []);

  /**
   * Rename a chat
   */
  const renameChat = useCallback(async (chatId: string, newTitle: string) => {
    try {
      const response = await ChatService.renameChat(chatId, newTitle);

      // Check if the API call was successful
      if (!response.success) {
        throw new Error(response.message || "Failed to rename chat");
      }

      // Update chat in list using new_name from response
      setChats((prev) =>
        prev.map((chat) =>
          chat.chat_id === chatId
            ? {
                ...chat,
                chat_name: response.new_name,
                updated_at: new Date().toISOString(),
              }
            : chat,
        ),
      );

      return response;
    } catch (err: any) {
      console.error("Failed to rename chat:", err);
      throw err;
    }
  }, []);

  /**
   * Refresh chat list
   */
  const refreshChats = useCallback(() => {
    fetchChats();
  }, [fetchChats]);

  /**
   * Send message to LLM
   * Used for real-time chat messaging
   */
  const sendMessage = useCallback(
    async (chatId: string, message: string, files?: File[]) => {
      try {
        const response = await LLMChatService.sendMessage({
          chat_id: chatId,
          message,
          files,
        });

        return {
          answer: response.answer,
          messageId: response.message_id,
          chatId: response.chat_id || chatId,
        };
      } catch (err: any) {
        console.error("Failed to send message:", err);
        throw err;
      }
    },
    [],
  );

  // Load chats on mount
  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  return {
    chats,
    loading,
    error,
    createChat,
    deleteChat,
    renameChat,
    refreshChats,
    sendMessage,
  };
}
